"use client";

import { Heart } from "lucide-react";
import { useTranslations, useFormatter } from "next-intl";

import { useProjectStatistics } from "@/app/[locale]/projects/_api/use-project-queries";
import { GlassCard } from "@/components/glass-card";
import { Skeleton } from "@/components/ui/skeleton";

const CURRENCY_FORMAT_OPTS = { style: 'currency', currency: 'EUR' } as const;

export function ProjectStatistics({ projectId }: { projectId: string }) {
  const t = useTranslations("projects");
  const format = useFormatter();
  const { data: statistics, isLoading } = useProjectStatistics(projectId);

  if (isLoading) {
    return (
      <GlassCard>
        <h3 className="text-xl font-semibold mb-4">{t("detail.statistics")}</h3>
        <div className="space-y-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </GlassCard>
    );
  }

  if (!statistics) {
    return null;
  }

  return (
    <GlassCard>
      <h3 className="text-xl font-semibold flex items-center gap-2 mb-4">
        <Heart className="h-5 w-5 text-red-500" />
        {t("detail.projectStatistics")}
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-primary">
            {statistics.supporterCount?.toLocaleString() || 0}
          </div>
          <div className="text-sm text-muted-foreground">{t("detail.supporters")}</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">
            {statistics.totalDonations && statistics.totalDonations > 0
              ? format.number(statistics.totalDonations, CURRENCY_FORMAT_OPTS)
              : '—'}
          </div>
          <div className="text-sm text-muted-foreground">{t("detail.collected")}</div>
        </div>
      </div>
    </GlassCard>
  );
}
